/**
 * Sparklink upload-modal deep links (client Assistant + Town Square Community panel).
 *
 * Example:
 * `/community?display_upload_modal=true&force_uploader_on_all_users=true&message=Analyze%20my%20art`
 */

export const SPARKLINK_PARAM_DISPLAY_UPLOAD_MODAL = 'display_upload_modal';
export const SPARKLINK_PARAM_FORCE_UPLOADER_ON_ALL_USERS = 'force_uploader_on_all_users';
export const SPARKLINK_PARAM_FORCE_UPLOADER_WHEN_ZERO_UPLOADS_ONLY = 'force_uploader_when_zero_uploads_only';
export const SPARKLINK_PARAM_MODAL_HEADER_TEXT = 'modal_header_text';
export const SPARKLINK_PARAM_MODAL_HEADER_SUBTEXT = 'modal_header_subtext';

export const SPARKLINK_UPLOAD_MODAL_PARAM_KEYS = [
  SPARKLINK_PARAM_DISPLAY_UPLOAD_MODAL,
  SPARKLINK_PARAM_FORCE_UPLOADER_ON_ALL_USERS,
  SPARKLINK_PARAM_FORCE_UPLOADER_WHEN_ZERO_UPLOADS_ONLY,
  SPARKLINK_PARAM_MODAL_HEADER_TEXT,
  SPARKLINK_PARAM_MODAL_HEADER_SUBTEXT,
] as const;

export type SparklinkUploadModalParams = {
  displayUploadModal?: boolean;
  forceOnAllUsers?: boolean;
  forceOnZeroUploadsOnly?: boolean;
  customHeaderText?: string;
  customSubtext?: string;
  sparkId?: string;
};

export function parseSparklinkUploadModalParams(
  input: URLSearchParams | string | Record<string, string | string[] | undefined | null>
): SparklinkUploadModalParams {
  const searchParams =
    input instanceof URLSearchParams
      ? input
      : typeof input === 'string'
        ? new URLSearchParams(input.startsWith('?') ? input.slice(1) : input)
        : new URLSearchParams(
            Object.entries(input).flatMap(([key, value]) => {
              if (value == null || value === '') return [];
              return Array.isArray(value) ? value.map((entry) => [key, entry]) : [[key, value]];
            })
          );

  const displayUploadModal = searchParams.get(SPARKLINK_PARAM_DISPLAY_UPLOAD_MODAL) === 'true' ? true : undefined;
  if (!displayUploadModal) {
    return {};
  }

  return {
    displayUploadModal: true,
    forceOnAllUsers: searchParams.get(SPARKLINK_PARAM_FORCE_UPLOADER_ON_ALL_USERS) === 'true' ? true : undefined,
    forceOnZeroUploadsOnly:
      searchParams.get(SPARKLINK_PARAM_FORCE_UPLOADER_WHEN_ZERO_UPLOADS_ONLY) === 'true' ? true : undefined,
    customHeaderText: searchParams.get(SPARKLINK_PARAM_MODAL_HEADER_TEXT) || undefined,
    customSubtext: searchParams.get(SPARKLINK_PARAM_MODAL_HEADER_SUBTEXT) || undefined,
    sparkId: searchParams.get('spark_id') || undefined,
  };
}

export function hasSparklinkUploadModalParam(
  input: URLSearchParams | string | Record<string, string | string[] | undefined | null>
): boolean {
  return parseSparklinkUploadModalParams(input).displayUploadModal === true;
}

export function stripSparklinkUploadModalParams(searchParams: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  for (const key of SPARKLINK_UPLOAD_MODAL_PARAM_KEYS) {
    next.delete(key);
  }
  return next;
}

export function shouldShowSparklinkUploadModal(params: {
  displayUploadModal?: boolean;
  /**
   * Whether the modal was already dismissed for this landing. This is
   * intentionally page-session-scoped (an in-memory latch), never persisted
   * to localStorage — a fresh click of the same sparklink is a fresh
   * document load and must re-open the modal every time.
   */
  hasDismissedLanding: boolean;
  isAuthReady: boolean;
  forceOnAllUsers?: boolean;
  forceOnZeroUploadsOnly?: boolean;
  hasZeroUploads: boolean;
}): boolean {
  if (!params.displayUploadModal || params.hasDismissedLanding || !params.isAuthReady) {
    return false;
  }

  if (params.forceOnAllUsers) {
    return true;
  }

  if (params.forceOnZeroUploadsOnly) {
    return params.hasZeroUploads;
  }

  // `display_upload_modal=true` alone (common marketing sparklinks) shows for everyone.
  return true;
}
